import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'isomorphic-dompurify';
import { ArrowLeft, Loader2, ExternalLink, Monitor, Tablet, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebsitePreview = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('desktop'); // desktop, tablet, mobile

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await axios.get(`${API}/website/projects/${projectId}`, {
          withCredentials: true
        });
        if (active) setProject(response.data);
      } catch (error) {
        if (active) navigate('/admin/dashboard');
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [projectId, navigate]);

  // Sanitize + memoize the custom CSS so the <style> prop object is stable across renders
  const customCssHtml = useMemo(
    () => ({ __html: project?.custom_styles ? DOMPurify.sanitize(project.custom_styles) : '' }),
    [project]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#7dd3fc] animate-spin" />
      </div>
    );
  }

  if (!project) return null;

  const globalStyles = project.generated_code?.globalStyles || {};
  const sections = project.sections || [];

  const getViewportWidth = () => {
    switch (viewMode) {
      case 'tablet': return '768px';
      case 'mobile': return '375px';
      default: return '100%';
    }
  };

  const renderSection = (section) => {
    const styles = section.styles || {};
    const bgColor = styles.backgroundColor || globalStyles.backgroundColor || '#1a1c1b';
    const textColor = styles.textColor || globalStyles.textColor || '#ffffff';
    const accentColor = styles.accentColor || globalStyles.primaryColor || '#7dd3fc';

    return (
      <section
        key={section.id}
        className="py-16 px-8"
        style={{ backgroundColor: bgColor, color: textColor }}
      >
        <div className="max-w-5xl mx-auto">
          {section.title && (
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: accentColor }}
            >
              {section.title}
            </h2>
          )}
          {section.subtitle && (
            <p className="text-lg opacity-80 mb-6">{section.subtitle}</p>
          )}
          {section.content && (
            <p className="text-base opacity-70 mb-8 max-w-2xl">{section.content}</p>
          )}
          
          {/* Render items if present */}
          {section.items && section.items.length > 0 && (
            <div className={`grid gap-6 ${
              section.layout === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
            }`}>
              {section.items.map((item, idx) => (
                <div
                  key={`${section.id}-item-${item.title || item.name || idx}`}
                  className="p-6 rounded-lg"
                  style={{ backgroundColor: `${accentColor}15`, borderLeft: `3px solid ${accentColor}` }}
                >
                  <h3 className="font-semibold text-lg mb-2" style={{ color: accentColor }}>
                    {item.title || item.name}
                  </h3>
                  <p className="opacity-70 text-sm">{item.description || item.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex flex-col">
      {/* Preview Header */}
      <header className="bg-[#1a1c1b] border-b border-[#1e3a5f]/50 sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/admin/editor/${projectId}`)}
              variant="ghost"
              className="text-[#888680] hover:text-[#7dd3fc]"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Editor
            </Button>
            <span className="text-[#7dd3fc] font-semibold">{project.name}</span>
          </div>

          {/* Viewport Switcher */}
          <div className="flex items-center gap-1 bg-[#302f2c] rounded-full p-1">
            <button
              onClick={() => setViewMode('desktop')}
              className={`p-2 rounded-full transition-all ${
                viewMode === 'desktop' ? 'bg-[#7dd3fc] text-[#1a1c1b]' : 'text-[#888680] hover:text-[#7dd3fc]'
              }`}
            >
              <Monitor size={18} />
            </button>
            <button
              onClick={() => setViewMode('tablet')}
              className={`p-2 rounded-full transition-all ${
                viewMode === 'tablet' ? 'bg-[#7dd3fc] text-[#1a1c1b]' : 'text-[#888680] hover:text-[#7dd3fc]'
              }`}
            >
              <Tablet size={18} />
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`p-2 rounded-full transition-all ${
                viewMode === 'mobile' ? 'bg-[#7dd3fc] text-[#1a1c1b]' : 'text-[#888680] hover:text-[#7dd3fc]'
              }`}
            >
              <Smartphone size={18} />
            </button>
          </div>

          <div className="w-32" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Preview Area */}
      <div className="flex-1 p-4 flex justify-center overflow-auto">
        <div
          className="bg-white shadow-2xl transition-all duration-300 overflow-hidden"
          style={{
            width: getViewportWidth(),
            maxWidth: '100%',
            minHeight: '600px',
            borderRadius: viewMode !== 'desktop' ? '20px' : '0'
          }}
        >
          {/* Rendered Website */}
          <div
            style={{
              fontFamily: globalStyles.fontFamily || 'Inter, sans-serif',
              backgroundColor: globalStyles.backgroundColor || '#1a1c1b',
              color: globalStyles.textColor || '#ffffff',
              minHeight: '100%'
            }}
          >
            {sections.length > 0 ? (
              sections.map(renderSection)
            ) : (
              <div className="flex items-center justify-center h-96 text-center">
                <div>
                  <p className="text-[#888680] mb-2">No sections yet</p>
                  <Button
                    onClick={() => navigate(`/admin/editor/${projectId}`)}
                    className="bg-[#7dd3fc] text-[#1a1c1b] rounded-full"
                  >
                    Add Sections
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      {project.custom_styles && (
        <style dangerouslySetInnerHTML={customCssHtml} />
      )}
    </div>
  );
};

export default WebsitePreview;
